import React, {
  memo,
} from 'react';
import Divider from '@mui/material/Divider';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import ToolTip from 'core/components/Tooltip';
import SplitActionButton from 'core/components/SplitActionButton';
import PrinterIcon from 'core/icons/printer';
import SaveIcon from 'core/icons/save';
import MasterTemplateIcon from 'core/icons/masterTemplate';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import Button, {
  Props as ButtonProps,
} from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  pluralize,
} from '@populateui/utils';
import {
  formatDateTime,
} from 'utils/date';
import {
  BillingState,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  useVisitNoteFormBridge,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/contexts/visitNoteFormBridge';
import VisitNoteDocumentActions, {
  SendDocumentsActions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteForms';
import ContentPasteOffOutlinedIcon from '@mui/icons-material/ContentPasteOffOutlined';

export const finalizedVisitNoteMessage = (
  'Note finalized. The PDF is being prepared and will be available in a few minutes.'
);

type BillingButtonProps = ButtonProps & { nextState?: BillingState };

type Props = {
  isDirty: boolean;
  dirtyFieldsCount: number;
  isSubmitting: boolean;
  isValid: boolean;
  savingVisitNote: boolean;
  unfinalizingVisitNote: boolean;
  isFinalized: boolean;
  sendDocumentsActions: SendDocumentsActions;
  isBillingReview: boolean;
  isBillingReviewCompleted: boolean;
  hasBillingCodes: boolean;
  updatedOn?: string;
  billingButtonProps: BillingButtonProps;
  onSave: () => void;
  onFinalize: () => void;
  onUnfinalize: () => void;
  onUpdateBillingState: () => void;
  onSaveAsTemplate: () => void;
  onPrint?: () => void;
  isPrinting?: boolean;
  isFinalizedPdfPending: boolean;
};

function VisitNoteFormActions({
  isDirty,
  dirtyFieldsCount,
  isSubmitting,
  isValid,
  savingVisitNote,
  unfinalizingVisitNote,
  isFinalized,
  sendDocumentsActions,
  isBillingReview,
  isBillingReviewCompleted,
  hasBillingCodes,
  updatedOn,
  billingButtonProps,
  onSave,
  onFinalize,
  onUnfinalize,
  onUpdateBillingState,
  onSaveAsTemplate,
  onPrint,
  isPrinting,
  isFinalizedPdfPending,
}: Props) {
  const { needsRefresh } = useVisitNoteFormBridge();

  return (
    <div className="relative flex min-w-0 items-center justify-between gap-4 overflow-x-auto border-t border-t-gray-200 p-4 nw-visit-note-footer-buttons">
      <div className="flex shrink-0 items-center gap-4">
        <SplitActionButton
          label="Save"
          onPrimaryClick={onSave}
          items={[
            {
              id: 'save-as-template',
              label: 'Save As Master Template',
              icon: MasterTemplateIcon,
              className: 'nw-save-master-template-button',
              onSelect: onSaveAsTemplate,
            },
            {
              id: 'save-note',
              label: 'Save Note',
              icon: SaveIcon,
              onSelect: onSave,
            },
          ]}
          disabled={needsRefresh || isSubmitting || savingVisitNote}
          startIcon={<SaveIcon className="!w-4 !h-4" />}
          caretDirection="up"
          paperClassName="!min-w-[12rem]"
          placement="top-start"
          classes={{ primary: 'nw-save-visit-note-button' }}
        />
        {!isFinalized ? (
          <Button
            className="disabled:!bg-blue-400 nw-visit-note-finalize-button !h-8"
            variant="contained"
            color="primary"
            type="button"
            disabled={needsRefresh || isSubmitting || !isValid || savingVisitNote}
            onClick={onFinalize}
            startIcon={<AssignmentTurnedInOutlinedIcon className="!w-4 !h-4 !text-inherit" />}
          >
            Finalize & Sign
          </Button>
        ) : (
          <Button
            className="disabled:!bg-blue-400 nw-visit-note-unfinalize-button !h-8"
            variant="contained"
            color="primary"
            type="button"
            disabled={needsRefresh || unfinalizingVisitNote || savingVisitNote}
            onClick={onUnfinalize}
            startIcon={<ContentPasteOffOutlinedIcon className="!w-4 !h-4 !text-inherit" />}
          >
            Un-finalize
          </Button>
        )}
        {!isNil(onPrint) && (
          <ToolTip
            title="Preparing PDF. Print will enable automatically when ready."
            hide={!isFinalizedPdfPending}
          >
            <Button
              onClick={onPrint}
              variant="outlined"
              disabled={isFinalizedPdfPending || isPrinting || isSubmitting}
              className="flex gap-2 items-center !px-3"
            >
              {isPrinting ? <Spinner /> : <PrinterIcon className="!w-4 !h-4" />}
              {isPrinting ? 'Printing...' : 'Print'}
            </Button>
          </ToolTip>
        )}
        <Divider
          orientation="vertical"
          flexItem
        />
        <ToolTip
          title="A visit note with no Service codes cannot be marked as Billing Review Completed"
          hide={hasBillingCodes || !isBillingReview}
        >
          <div className={clsx({
            'cursor-not-allowed': (isBillingReview && !hasBillingCodes)
              || isBillingReviewCompleted,
          })}
          >
            <Button
              {...billingButtonProps}
              disabled={needsRefresh
                || savingVisitNote
                || isBillingReviewCompleted
                || (isBillingReview && !hasBillingCodes)}
              onClick={onUpdateBillingState}
            />
          </div>
        </ToolTip>
        <VisitNoteDocumentActions
          {...sendDocumentsActions}
          disabled={needsRefresh || isSubmitting || savingVisitNote || unfinalizingVisitNote}
        />
      </div>
      <div
        className={clsx(
          'shrink-0 text-xs invisible',
          { '!visible': !isSubmitting && !savingVisitNote && !isNil(updatedOn) },
        )}
      >
        {(isDirty && dirtyFieldsCount > 0) && (
          <p className="w-max">
            <span className="bg-secondary rounded text-white px-1.5 py-0.5 font-semibold">
              {dirtyFieldsCount}
            </span>
            {' '}
            {pluralize('unsaved change', dirtyFieldsCount, false)}
          </p>
        )}
        Last saved at
        {' '}
        <strong>{formatDateTime(updatedOn ?? '')}</strong>
      </div>
    </div>
  );
}

export default memo(VisitNoteFormActions);
